import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { retryFailedCall } from '@common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import {
  AccountByCompany,
  CompanyApi,
  CompanyUser,
  DeviceIds,
  EquipmentApi,
  EquipmentApiPicker,
  GeneralManagementApi,
  InvitationsApi,
  MonetizationApi,
  OwnershipType,
  PaidFeatureScope,
  PurchasesApiPicker,
  ViError,
  VitoguideBackendSettingsApi,
} from '@vi-iot/client';

import { ViToastService } from '@vi-iot/ui';

import { RoutingService, UIService } from '@services';
import {
  DevicesActions,
  DevicesStore,
  HemsActions,
  InstallationDetailsActions,
  InstallationDetailsStore,
  InstallationDevicePanels,
  RemoteConfigStore,
  RouterStore,
} from '@store';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import {
  LiveUpdatesService,
  LiveUpdatesUnsubscribeType,
} from '../../shared/live-updates/live-updates.service';
import { ActionMessagesService } from '../_services/action-messages.service';
import { AuthStore } from '../auth/auth.store';
import { LanguageStore, SUPPORTED_LANGUAGE } from '../settings';
import { InstallationDetailsMappers } from './installation-details.mappers';

interface KeyValueStorageObject<T> {
  data: T;
}

@Injectable()
export class InstallationDetailsEffects {
  private static PANELS_ORDER_BASE_URL = 'installationPanelsOrder';

  savePanelOrder$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.SavePanelsOrder),
      concatLatestFrom(() => [
        this.installationDetailsStore.panelsOrder$,
        this.installationDetailsStore.gatewaySerial$,
      ]),
      switchMap(([action, panels, gatewaySerial]) => {
        let payload: KeyValueStorageObject<InstallationDevicePanels> = {
          data: panels,
        };
        payload.data[action.deviceId] = action.panelsOrder;

        // Uncomment to reset panels order on save
        // payload = {
        //   data: {
        //     dashboard: defaultPanelsOrder
        //   }
        // };

        return this.settingApi
          .setKeyValueStorage(
            `${InstallationDetailsEffects.PANELS_ORDER_BASE_URL}${gatewaySerial}`,
            payload
          )
          .pipe(
            map(() => InstallationDetailsActions.SavePanelsOrderSuccess()),
            catchError((err) => of(InstallationDetailsActions.SavePanelsOrderError({ error: err })))
          );
      })
    );
  });

  savePanelsOrderMobile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.SavePanelsOrderMobile),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([action, gatewaySerial]) =>
        this.settingApi
          .setKeyValueStorage(
            `${InstallationDetailsEffects.PANELS_ORDER_BASE_URL}${gatewaySerial}`,
            { data: action.panelsOrder }
          )
          .pipe(
            retryFailedCall(3),
            map(() => action.panelsOrder)
          )
          .pipe(
            mergeMap((panelsOrder) => [
              InstallationDetailsActions.SavePanelsOrderMobileSuccess(),
              InstallationDetailsActions.GetPanelsOrderSuccess({ panelsOrder }),
            ]),
            catchError(() => of(InstallationDetailsActions.SavePanelsOrderMobileError()))
          )
      )
    );
  });

  getPanelsOrder$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetPanelsOrder),
      switchMap((action) =>
        this.settingApi
          .getKeyValueStorage(
            `${InstallationDetailsEffects.PANELS_ORDER_BASE_URL}${action.gatewaySerial}`
          )
          .pipe(
            retryFailedCall(3),
            map((response: KeyValueStorageObject<InstallationDevicePanels>) => {
              let panelsOrder = response.data;
              // Convert array to object due to cascade dashboard refactor VIG-3677
              if (Array.isArray(panelsOrder)) {
                panelsOrder = { dashboard: panelsOrder };
              }

              return InstallationDetailsActions.GetPanelsOrderSuccess({ panelsOrder });
            }),
            catchError((error) => of(InstallationDetailsActions.GetPanelsOrderError({ error })))
          )
      )
    );
  });

  getViCarePlans$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetViCarePlans),
      switchMap((action) =>
        this.purchasesApiPicker
          .instance()
          .getViCarePlans(action.installationId)
          .pipe(
            map((plans) => InstallationDetailsMappers.uniqueViCarePlans(plans)),
            map((plans) => InstallationDetailsActions.GetViCarePlansSuccess({ plans })),
            catchError((error) => of(InstallationDetailsActions.GetViCarePlansError({ error })))
          )
      )
    );
  });

  triggerGetMonetizationFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.TriggerGetMonetizationFeatures),
      concatLatestFrom(() => [
        this.remoteConfigStore.isVicarePlansTileAvailable$,
        this.authStore.isRsaUser(),
      ]),
      filter(
        ([_action, isVicarePlansTileAvailable, isRsaUser]) =>
          isVicarePlansTileAvailable || isRsaUser
      ),
      map(([action, _isVicarePlansTileAvailable, _isRsaUser]) =>
        InstallationDetailsActions.GetMonetizationFeatures({
          installationId: action.installationId,
        })
      )
    );
  });

  getMonetizationFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetMonetizationFeatures),
      switchMap((action) =>
        this.monetizationApi.getMonetizationFeatures(action.installationId).pipe(
          map((monetizationFeatures) =>
            InstallationDetailsActions.GetMonetizationFeaturesSuccess({ monetizationFeatures })
          ),
          catchError((error) =>
            of(InstallationDetailsActions.GetMonetizationFeaturesError({ error }))
          )
        )
      )
    );
  });

  loadDevices$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
      map(({ payload: { installation } }) => installation),
      map((installation) => InstallationDetailsMappers.mapAllDevices(installation)),
      map((devices) => InstallationDetailsMappers.mapToDevicesWithFeatures(devices)),
      map((devices) => DevicesActions.loadDevicesSuccess({ devices }))
    );
  });

  initDeviceSelection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
      concatLatestFrom(() => this.routerStore.selectQueryParams$.pipe(first())),
      map(([{ payload: { installation } }, queryParams]) => [installation, queryParams]),
      map(([installation, queryParams]) => {
        let { gatewaySerial } = queryParams as Params;
        if (!gatewaySerial) {
          gatewaySerial = installation?.gateways?.find((gateway) =>
            gateway?.devices?.find((device) => device?.isSelected)
          )?.serial;

          if (!gatewaySerial) {
            // is stanalone installation with no main devices
            gatewaySerial = installation?.gateways?.[0]?.serial;
          }
        }

        return [installation, gatewaySerial];
      }),
      tap(([_installation, gatewaySerial]) => {
        this.store.dispatch(InstallationDetailsActions.SelectDeviceOnStartup({ gatewaySerial }));
      }),
      map(([installation, gatewaySerial]) => [
        InstallationDetailsMappers.mapAllDevices(installation),
        gatewaySerial,
      ]),
      map(([devices, gatewaySerial]) =>
        devices.find(
          (device) =>
            device.gatewaySerial === gatewaySerial &&
            (device.isMainDeviceOfGateway || device.isStandalone)
        )
      ),
      map((deviceToSelect) =>
        DevicesActions.setSelectedDevice({
          gatewaySerial: deviceToSelect?.gatewaySerial,
          deviceId: deviceToSelect?.id,
        })
      )
    );
  });

  readHEMSDevice$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
      map(({ payload: { installation } }) => ({
        installationId: installation.id,
        hemsDevice: InstallationDetailsMappers.mapAllDevices(installation).find(
          (device) => device.id === DeviceIds.Hems
        ),
      })),
      filter(({ hemsDevice }) => !!hemsDevice),
      map(({ hemsDevice }) =>
        HemsActions.setHemsGatewaySerial({ gatewaySerial: hemsDevice.gatewaySerial })
      )
    );
  });

  readViCarePlans$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
      // Don't fetch ViCare Plans if installation is without ViCare user or owned by maintainer
      switchMap((action) =>
        combineLatest([
          this.remoteConfigStore.isVicarePlansTileAvailable$,
          this.authStore.isRsaUser(),
          this.authStore.isEndUser$,
          this.installationDetailsStore.ownershipType$,
        ]).pipe(
          first(
            ([isVicarePlansTileAvailable, isRsaUser, isEKUser, ownershipType]) =>
              !isEKUser &&
              (isRsaUser || isVicarePlansTileAvailable) &&
              ownershipType !== OwnershipType.PreCommissioning &&
              ownershipType !== OwnershipType.Oma
          ),
          map(() =>
            InstallationDetailsActions.GetViCarePlans({
              installationId: action.payload.installation.id,
            })
          )
        )
      )
    );
  });

  readInstallationDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InstallationDetailsActions.ReadInstallationDetails,
        DevicesActions.updateBoilerSerialSuccess,
        InstallationDetailsActions.deregisterGatewaySuccess
      ),
      tap(() => {
        this.liveUpdatesService.unsubscribeFeatures(
          LiveUpdatesUnsubscribeType.InstallationAggregatedStatus
        );
      }),
      tap(() => {
        this.appInsights.logEvent(
          APP_INSIGHTS_FEATURES.GET_INSTALLATION_FEATURES,
          LogLevelEnum.TRACE
        );
      }),
      switchMap((action) =>
        forkJoin([
          this.equipmentApi.getInstallation(action.installationId),
          this.equipmentApi.getInstallationAccessList(action.installationId),
        ]).pipe(
          map(([installation, accessList]) =>
            InstallationDetailsActions.ReadInstallationDetailsSuccess({
              payload: {
                accessList: accessList.data,
                installation: InstallationDetailsMappers.mapInstallationResponse(installation.data),
              },
            })
          ),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_FEATURES.GET_INSTALLATION_FEATURES_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            if (error?.statusCode === 404) {
              return of(InstallationDetailsActions.NotFound());
            }

            return of(
              InstallationDetailsActions.ReadInstallationDetailsError({ error: error?.error })
            );
          })
        )
      )
    );
  });

  getEmployeesList$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.getEmployeesList.attempt),
      switchMap((action) =>
        this.companyApi.getCompanyClients(`${action.customerNumber}`).pipe(
          tap((response: CompanyUser[]) => {
            if (response.length === 0) {
              this.viToastService.info({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATION.ACCESS_LIST.MAINTAINER.NO_EMPLOYEES'
                ),
              });
            }
          }),
          map((response: CompanyUser[]) =>
            InstallationDetailsActions.getEmployeesList.success({
              employees: response,
              customerNumber: action.customerNumber,
            })
          ),
          catchError((error) => {
            this.viToastService.error({
              message: this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.ACCESS_LIST.MAINTAINER.FAILED_TO_LOAD'
              ),
            });

            return of(
              InstallationDetailsActions.getEmployeesList.error({
                error: error?.error,
                customerNumber: action.customerNumber,
              })
            );
          })
        )
      )
    );
  });

  notFound$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InstallationDetailsActions.NotFound),
        tap(() => this.router.navigate(['/not-found']))
      );
    },
    { dispatch: false }
  );

  setGatewaySerialAsQueryParam$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InstallationDetailsActions.SelectDeviceOnStartup, DevicesActions.setSelectedDevice),
        switchMap((action) =>
          combineLatest([
            of(action),
            this.routingService.isNavigationFinished$.pipe(
              // If opened directly from url router.url remains equal to '/' until guards allow to navigate
              filter((isNavigationFinished) => isNavigationFinished && this.router.url !== '/'),
              take(1)
            ),
            this.installationDetailsStore.installationGateways$,
            this.devicesStore.mainDevicesForHeaderTiles$,
          ]).pipe(take(1))
        ),
        map(([action, _navigationEvent, gateways, devicesTiles]) => {
          const queryParams: Params = { gatewaySerial: action.gatewaySerial };

          const isGatewaySerialPresentInGatewayList = gateways.some(
            (gateway) => gateway.serial === action.gatewaySerial
          );

          if (!isGatewaySerialPresentInGatewayList && action.gatewaySerial) {
            const firstTileData = devicesTiles[0];

            return this.store.dispatch(
              DevicesActions.setSelectedDevice({
                gatewaySerial: firstTileData?.gatewaySerial,
                deviceId: firstTileData?.id,
              })
            );
          }

          if (
            queryParams.gatewaySerial &&
            queryParams.gatewaySerial !== this.getGatewaySerialFromUrl()
          ) {
            this.router.navigate([], {
              queryParams,
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  readUserPaidFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.setSelectedDevice, DevicesActions.setSelectedDeviceOnUserClick),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.devicesStore.selectedDevice$.pipe(take(1)),
          this.authStore.isRsaUser().pipe(filter((res) => res !== null)),
        ])
      ),
      filter(([_action, _device, isRsa]) => !isRsa),
      switchMap(([_action, device]) => {
        const boilerSerial = device?.boilerSerial;

        if (!boilerSerial) {
          return of(
            InstallationDetailsActions.ReadUserPaidFeaturesError({ error: 'boilerSerial missing' })
          );
        }

        return this.purchasesApiPicker
          .instance()
          .getAvailablePaidFeaturesForSingleInstallation(PaidFeatureScope.device, boilerSerial)
          .pipe(
            map((deviceTable) => deviceTable[0]),
            map((deviceWithPaidFeatures) =>
              InstallationDetailsActions.ReadUserPaidFeaturesSuccess({
                deviceWithPaidFeatures,
              })
            ),
            catchError((err) =>
              of(InstallationDetailsActions.ReadUserPaidFeaturesError({ error: err?.error }))
            )
          );
      })
    );
  });

  subscribeToAggregatedStatusChanges$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DevicesActions.loadDevicesSuccess),
        concatLatestFrom(() => this.devicesStore.mainDevices$),
        map(([_action, mainDevices]) => {
          const gatewaySerials = mainDevices
            ?.filter((device) => device?.gatewaySerial)
            .map((device) => device.gatewaySerial);

          this.liveUpdatesService.subscribeInstallationAggregatedStatus(gatewaySerials);
        })
      );
    },
    { dispatch: false }
  );

  stopMonitoring$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.StopMonitoring),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.authStore.email$.pipe(take(1)),
        ])
      ),
      tap(() => this.uiService.blockUI),
      switchMap(([action, installationId, email]) =>
        this.invitationsApi.unfollowInstallation(installationId, email).pipe(
          tap(() => this.uiService.unblockUI()),
          tap(() => {
            this.router.navigate(['/installations'], {
              queryParams: {
                unfollowedId: installationId,
              },
            });
          }),
          tap(() => this.actionMessagesService.onStopMonitoringSuccess(action.ownerName)),
          map(() => InstallationDetailsActions.StopMonitoringSuccess()),
          catchError((err) => {
            this.uiService.unblockUI();
            this.actionMessagesService.onStopMonitoringError();

            return of(InstallationDetailsActions.StopMonitoringError({ error: err?.error }));
          })
        )
      )
    );
  });

  getMaintenanceChecklist$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetMaintenanceChecklist),
      concatLatestFrom(() => [
        this.installationDetailsStore.device$,
        this.authStore.me$,
        this.languageStore.languageCode$,
        this.authStore.isDemo$,
      ]),
      tap(() => this.uiService.blockUI),
      switchMap(([, state, authMeState, languageCode, isDemo]) => {
        const boilerSerial = state.boilerSerial;
        const countryCode = authMeState?.companyCountryCode || 'DE';
        const language = languageCode.split('-')[0];

        if (isDemo && language !== 'de') {
          this.uiService.unblockUI();

          return of(
            InstallationDetailsActions.GetMaintenanceChecklistError({ error: 'Empty response' })
          );
        }

        return this.equipmentApi.getMaintenanceChecklist(boilerSerial, countryCode, language).pipe(
          tap(() => this.uiService.unblockUI()),
          map((response) =>
            InstallationDetailsActions.GetMaintenanceChecklistSuccess({
              maintenanceChecklist: response,
            })
          ),
          catchError((err) => {
            this.uiService.unblockUI();

            return of(
              InstallationDetailsActions.GetMaintenanceChecklistError({ error: err?.error })
            );
          })
        );
      })
    );
  });

  getServiceInstruction$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetServiceInstruction),
      concatLatestFrom(() => [
        this.installationDetailsStore.device$,
        this.installationDetailsStore.isInstallationVitocrossalNAType$,
        this.languageStore.languageCode$,
      ]),
      tap(() => this.uiService.blockUI()),
      mergeMap(([{ documentTypes }, state, isVitocrossalNAType, languageCode]) => {
        const boilerSerial = state.boilerSerial;
        const languageCodeSplit = languageCode.split('-');
        const canadaCountryCode = SUPPORTED_LANGUAGE.CA.split('-')[1];
        /*  VIG-7910: E3 Vitocrossal is selling on US/CA market, so we have to switch country code in this case */
        const countryCode = isVitocrossalNAType ? canadaCountryCode : languageCodeSplit[1];
        const language = languageCodeSplit[0];
        const documentTypesParams = documentTypes
          .reduce((acc, documentType) => acc.concat(`documentType=${documentType}`), [])
          .join('&');

        return this.equipmentApi
          .getDocumentsOfTypes(boilerSerial.slice(0, 7), documentTypesParams, countryCode, language)
          .pipe(
            tap(() => this.uiService.unblockUI()),
            map((response) => {
              return response.length > 0
                ? InstallationDetailsActions.GetServiceInstructionSuccess({
                    serviceInstruction: response,
                  })
                : InstallationDetailsActions.GetServiceInstructionDefaultLanguage.attempt({
                    documentTypes,
                  });
            }),
            catchError((err) => {
              this.uiService.unblockUI();

              return err.statusCode === 500
                ? of(
                    InstallationDetailsActions.GetServiceInstructionDefaultLanguage.attempt({
                      documentTypes,
                    })
                  )
                : of(InstallationDetailsActions.GetServiceInstructionError({ error: err?.error }));
            })
          );
      })
    );
  });

  getServiceInstructionInDefaultLanguage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.GetServiceInstructionDefaultLanguage.attempt),
      concatLatestFrom(() => [this.installationDetailsStore.device$]),
      tap(() => this.uiService.blockUI),
      mergeMap(([{ documentTypes }, state]) => {
        const boilerSerial = state.boilerSerial;
        const documentTypesParams = documentTypes
          .reduce((acc, documentType) => acc.concat(`documentType=${documentType}`), [])
          .join('&');

        return this.equipmentApi.getDocumentsOfTypes(boilerSerial, documentTypesParams).pipe(
          tap(() => this.uiService.unblockUI()),
          map((response) =>
            InstallationDetailsActions.GetServiceInstructionDefaultLanguage.success({
              serviceInstruction: response,
            })
          ),
          catchError((err) => {
            this.uiService.unblockUI();

            return of(
              InstallationDetailsActions.GetServiceInstructionDefaultLanguage.error({
                error: err?.error,
              })
            );
          })
        );
      })
    );
  });

  updateBuildingInfo$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.updateBuildingInfo.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.state$.pipe(take(1)),
        ])
      ),
      tap(() => this.uiService.blockUI()),
      switchMap(([action, installationId, state]) => {
        return this.equipmentApi
          .updateInstallation(installationId, {
            ...state.installation.value,
            ...action.buildingInfo,
          })
          .pipe(
            tap(() => this.uiService.unblockUI()),
            map((response) =>
              InstallationDetailsActions.updateBuildingInfo.success({
                buildingInfo: {
                  buildingPhone: response.data.buildingPhone,
                  buildingName: response.data.buildingName,
                  buildingEmail: response.data.buildingEmail,
                  address: response.data.address,
                },
              })
            ),
            catchError((error) =>
              of(InstallationDetailsActions.updateBuildingInfo.error({ error }))
            )
          );
      })
    );
  });

  deregisterGateway$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.deregisterGateway),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.generalManagementApi.deleteGateway(action.installationId, action.gatewaySerial).pipe(
          map(() => {
            this.uiService.unblockUI();

            return InstallationDetailsActions.deregisterGatewaySuccess({
              installationId: action.installationId,
              gatewaySerial: action.gatewaySerial,
            });
          }),
          catchError(() => {
            this.uiService.unblockUI();

            return of(InstallationDetailsActions.deregisterGatewayError());
          })
        )
      )
    );
  });

  refreshDeviceList$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.refreshDeviceList),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.equipmentApiPicker
          .instance()
          .refreshDeviceList(action.installationId, action.gatewaySerial)
          .pipe(
            map(() => {
              this.uiService.unblockUI();
              this.viToastService.info({
                message: this.translateService.instant('REFRESH_DEVICE_LIST.SNACKBAR.SUCCESS_MSG'),
              });

              return InstallationDetailsActions.refreshDeviceListSuccess();
            }),
            catchError((error: ViError) => {
              this.uiService.unblockUI();
              if (error.statusCode !== 404) {
                this.viToastService.error({
                  message: this.translateService.instant('REFRESH_DEVICE_LIST.SNACKBAR.ERROR_MSG'),
                });
              }

              return of(InstallationDetailsActions.refreshDeviceListError());
            })
          )
      )
    );
  });

  listCompanyUsersSearchResult$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.getAccountsByCompany.attempt),
      concatLatestFrom(() => [this.installationDetailsStore.accessList$]),
      switchMap(([_action, accessList]) => {
        const observableCompanyClientArray: Observable<{
          success: boolean;
          companyNumber: string;
          data: CompanyUser[];
        }>[] = [];
        const listCompanyNumber: string[] = [
          accessList?.commercialOwner?.customerNumber,
          ...accessList.partners.map((partner) => partner?.customerNumber),
          accessList?.buildingManager?.customerNumber,
          accessList?.serviceContractor?.customerNumber,
          accessList?.operator?.customerNumber,
        ];

        listCompanyNumber.forEach((companyNumber) => {
          if (companyNumber) {
            observableCompanyClientArray.push(
              this.companyApi.getCompanyClients(companyNumber).pipe(
                map((response: CompanyUser[]) => {
                  return {
                    success: true,
                    companyNumber: companyNumber,
                    data: response,
                  };
                }),
                catchError(() =>
                  of({
                    success: false,
                    companyNumber: companyNumber,
                    data: [] as CompanyUser[],
                  })
                )
              )
            );
          }
        });

        return forkJoin(observableCompanyClientArray);
      }),
      map((responseCompanyClientArray) => {
        const isAllSubscriptionsFailed = responseCompanyClientArray.every(
          (subscription) => !subscription?.success
        );

        if (isAllSubscriptionsFailed) {
          return InstallationDetailsActions.getAccountsByCompany.error();
        }

        const accountsByCompany: AccountByCompany = {};

        responseCompanyClientArray.forEach((companyClient) => {
          if (companyClient.data.length > 0 && !accountsByCompany[companyClient.companyNumber]) {
            accountsByCompany[companyClient.companyNumber] = companyClient.data;
          }
        });

        return InstallationDetailsActions.getAccountsByCompany.success({ accountsByCompany });
      })
    );
  });

  constructor(
    private router: Router,
    private routerStore: RouterStore,
    private equipmentApi: EquipmentApi,
    private equipmentApiPicker: EquipmentApiPicker,
    private generalManagementApi: GeneralManagementApi,
    private monetizationApi: MonetizationApi,
    private invitationsApi: InvitationsApi,
    private actions$: Actions,
    private uiService: UIService,
    private installationDetailsStore: InstallationDetailsStore,
    private settingApi: VitoguideBackendSettingsApi,
    private authStore: AuthStore,
    private liveUpdatesService: LiveUpdatesService,
    private actionMessagesService: ActionMessagesService,
    private languageStore: LanguageStore,
    private store: Store,
    private devicesStore: DevicesStore,
    private purchasesApiPicker: PurchasesApiPicker,
    private remoteConfigStore: RemoteConfigStore,
    private appInsights: AppInsightsMonitoringService,
    private routingService: RoutingService,
    private viToastService: ViToastService,
    private translateService: TranslateService,
    private companyApi: CompanyApi
  ) {}

  private getGatewaySerialFromUrl(): string {
    const currentUrl = window.location.href;
    const indexOfGwSerial = window.location.href.indexOf('gatewaySerial');
    if (indexOfGwSerial !== -1) {
      // +14 to skip 'gatewaySerial=' substring, +30 to cut 16 numbers of gatewaySerial
      return currentUrl.slice(indexOfGwSerial + 14, indexOfGwSerial + 30);
    }

    return '';
  }
}
